import React, { useState } from "react";

import * as Styled from "./EssentialsCard.styles";
import AppPlaceholderPhoto from "../../../../../../common/assets/images/shared-icons/app-icon-placeholder.svg";
import { AdministrationCard } from "../../../../../../common/components/AdministrationCard/AdministrationCard";
import Button from "../../../../../../common/components/Button/Button";
import { GridTable } from "../../../../../../common/components/GridTable/GridTable";
import Input from "../../../../../../common/components/Input/Input";
import { ToggleSection } from "../../../../../../common/components/ToggleSection/ToggleSection";
import { PlatformName } from "../../../../../../common/models";
import { App } from "../../../../../../common/models/App.model";
import { Flex } from "../../../../../../common/styles/reusable/Flex/Flex.styles";
import { getAppPlatformOsLabel } from "../../../../../../common/util/contentResolvers";
import identifiers from "../../../../../../common/util/identifiers.json";
import { useShakingAnimation } from "../../../../../context/ShakingAnimation/ShakingAnimationContext";
import LogoComponent from "../../../../shared/components/LogoComponent/LogoComponent";
import GeneralCardRow from "../../../views/GeneralView/GeneralClientRow";

interface Props {
  selectedApp: App;
  appName: string;
  updateApp: (newName: string) => Promise<void>;
  updateAppLogo: (file: File, toggle: () => void) => void;
  deleteAppLogo: (toggle: () => void) => void;
  loading: boolean;
  saving: boolean;
  uploading: boolean;
}

export function EssentialsCard({
  selectedApp,
  appName,
  updateApp,
  loading,
  saving,
  updateAppLogo,
  deleteAppLogo,
  uploading,
}: Props) {
  const [newAppName, setNewAppName] = useState<string>(appName);
  const { triggerShake } = useShakingAnimation();

  const isButtonDisabled = selectedApp.name === newAppName || !newAppName.trim().length;
  const tooltipText = selectedApp.is_sample
    ? "Sample app can't be modified"
    : selectedApp.name === newAppName
    ? "Please change the app name first"
    : "Please enter app name first";

  return (
    <AdministrationCard
      title="Essentials"
      subtitle="If you don't like the read-only essentials listed below, mark this app as inactive and create a new app."
      testId={identifiers["essentials.header"]}
    >
      <Styled.InputContainer $paddingBottom={selectedApp.platform.name === PlatformName.WEB ? 2 : 1}>
        <Flex
          $column
          $gap={2}
        >
          <LogoComponent
            title="App icon"
            subtitle="Optional • Visible to workspace members only."
            image={selectedApp?.logo_url}
            placeholderImage={AppPlaceholderPhoto}
            onUpdatePicture={updateAppLogo}
            onDeletePicture={deleteAppLogo}
            loading={uploading}
            squareCanvas
            canChange={!selectedApp.is_sample}
            tooltip={selectedApp.is_sample ? "Sample app can't be modified" : undefined}
          />
          <Input
            label="App name"
            value={newAppName}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setNewAppName(e.currentTarget.value)}
            testId={identifiers["essentials.input.appName"]}
          />
          {selectedApp.platform.name !== PlatformName.WEB && (
            <GridTable gridTemplateColumns="repeat(3, auto)">
              {!Boolean(selectedApp?.is_sample) && (
                <GeneralCardRow
                  title={"Bundle ID"}
                  value={selectedApp?.bundle_id ?? ""}
                  toastText={"Bundle ID copied to clipboard"}
                  testId={identifiers["essentials.input.bundleId"]}
                />
              )}
              <GeneralCardRow
                title="Platform"
                value={selectedApp ? getAppPlatformOsLabel(selectedApp?.platform) : ""}
                toastText="Platform copied to clipboard"
                testId={identifiers["essentials.input.platform"]}
              />
            </GridTable>
          )}
        </Flex>
      </Styled.InputContainer>

      <Button
        type="submit"
        size="small"
        disabled={isButtonDisabled || loading}
        onClick={() => (selectedApp.is_sample ? triggerShake("AddAppButton") : updateApp(newAppName))}
        testId={identifiers["essentials.button.save"]}
        tooltip={{
          position: "top",
          text: tooltipText,
          disabled: !selectedApp.is_sample && !isButtonDisabled,
        }}
        loadingText={saving && "Saving..."}
      >
        Save changes
      </Button>
    </AdministrationCard>
  );
}
